import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import { getIntegrationsStatus } from '@/lib/api';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { GarminDerivedAttribution } from '@/components/activities/GarminAttribution';
import { AppleHealthDerivedAttribution } from '@/components/activities/AppleHealthAttribution';

// Types for Coach Dashboard
interface WeeklyLoad {
  week: string;
  level: 'low' | 'medium' | 'high';
  value: number;
}

interface Risk {
  type: 'success' | 'warning' | 'info';
  message: string;
}

// Athlete Dashboard Components
import { AppLayout } from '@/components/layout/AppLayout';
import { DailyDecisionCard } from '@/components/dashboard/DailyDecisionCard';
import { DailyAdaptationBanner } from '@/components/dashboard/DailyAdaptationBanner';
import { ConsistencyRing } from '@/components/dashboard/ConsistencyRing';
import { DeviceConnectionPrompt } from '@/components/dashboard/DeviceConnectionPrompt';
import { OnboardingProgressCard } from '@/components/dashboard/OnboardingProgressCard';
import { StreakBadge } from '@/components/dashboard/StreakBadge';
import { WeeklyCompletionRing } from '@/components/dashboard/WeeklyCompletionRing';
import { PageHeader } from '@/components/layout/PageHeader';
import { TodayWorkoutCard } from '@/components/dashboard/TodayWorkoutCard';
import { useSyncTodayWorkout } from '@/hooks/useSyncTodayWorkout';
import { useDashboardData } from '@/hooks/useDashboardData';
import { PlanCoachChat } from '@/components/plan/PlanCoachChat';
import { fetchProfileCompleteness, type ProfileCompletenessData } from '@/lib/analyticsApi';
import { format } from 'date-fns';

// Coach Dashboard Components
import { useCoachDashboardData, useAthleteSelection } from '@/hooks/useCoachDashboardData';
import { CoachKpiCard } from '@/components/coach/CoachKpiCard';
import { AdherenceChart } from '@/components/coach/AdherenceChart';
import { WeeklyLoadChart } from '@/components/coach/WeeklyLoadChart';
import { RiskList } from '@/components/coach/RiskList';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/form/select';
import { Loader2 } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  getAdherenceVariant,
  getAvatarInitials,
  getLoadTrendDisplay,
  getRiskVariant,
} from '@/lib/coach-dashboard-format';

/**
 * Athlete Dashboard - Telemetry-first execution view
 */
function AthleteDashboard() {
  useSyncTodayWorkout();
  const { t } = useTranslation();
  const dashboardData = useDashboardData();
  const { data: integrationsStatus } = useAuthenticatedQuery({
    queryKey: ['integrations', 'status'],
    queryFn: getIntegrationsStatus,
    staleTime: 2 * 60 * 1000,
  });
  const { data: profileCompleteness } = useAuthenticatedQuery<ProfileCompletenessData>({
    queryKey: ['profile', 'completeness'],
    queryFn: fetchProfileCompleteness,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const garminConnected = integrationsStatus?.integrations?.some(
    (i: { provider: string; connected: boolean }) => i.provider === 'garmin' && i.connected
  ) ?? false;
  const appleHealthConnected = integrationsStatus?.integrations?.some(
    (i: { provider: string; connected: boolean }) => i.provider === 'apple_health' && i.connected
  ) ?? false;

  const today = format(new Date(), 'EEEE, MMMM d');

  return (
    <AppLayout>
      <div className="space-y-1">
        <PageHeader
          title={t('nav.today')}
          subtitle={today}
          statusLabel={t('today.statusLabel')}
        />
        {garminConnected && (
          <p className="text-[9px] text-muted-foreground/70 leading-tight -mt-1 mb-1">
            Insights derived in part from Garmin device-sourced data.
          </p>
        )}

        {/* Main content - edge-to-edge telemetry layout */}
        <div className="flex flex-col gap-4 max-w-[960px]">
          {/* 1. Hero Verdict — sticky HUD on mobile */}
          <div className="sticky top-[1.5rem] z-30 md:static md:z-auto -mx-4 px-4 py-2 md:p-0 md:bg-transparent bg-[hsl(var(--app-bg))]/90 backdrop-blur-md md:backdrop-blur-none border-b border-white/[0.04] md:border-b-0">
            <DailyDecisionCard
              todayIntelligence={dashboardData.todayIntelligence}
              isLoading={dashboardData.todayIntelligenceLoading}
              error={dashboardData.todayIntelligenceError}
              compact
            />
          </div>
          
          {/* 2. Daily Adaptation Banner — shows when engine recommends changes */}
          <DailyAdaptationBanner
            adaptation={dashboardData.todayAdaptation}
            isLoading={dashboardData.todayAdaptationLoading}
          />

          {/* 3. Today's Session */}
          <TodayWorkoutCard
            todayData={dashboardData.todayData}
            isLoading={dashboardData.todayDataLoading}
            error={dashboardData.todayDataError}
            trainingLoad7d={dashboardData.trainingLoad7d}
            activities10={dashboardData.activities10}
            todayIntelligence={dashboardData.todayIntelligence}
          />

          {/* 4. Gamification strip — streak, weekly completion, consistency */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <StreakBadge />
            <WeeklyCompletionRing />
            <div className="col-span-2 md:col-span-1">
              <ConsistencyRing />
            </div>
          </div>

          {/* Post-onboarding: Device connection prompt (for users who skipped) */}
          <DeviceConnectionPrompt hasDevice={garminConnected || appleHealthConnected} />

          {/* Post-onboarding: Profile completion progress */}
          <OnboardingProgressCard
            hasDevice={garminConnected || appleHealthConnected}
            hasInjuryInfo={profileCompleteness?.hasInjuryInfo ?? false}
            hasExperience={profileCompleteness?.hasExperience ?? false}
            hasRaceTarget={profileCompleteness?.hasRaceTarget ?? false}
          />

          {garminConnected && (
            <div className="pt-3 border-t border-white/[0.06]">
              <GarminDerivedAttribution />
            </div>
          )}
          {appleHealthConnected && (
            <div className="pt-3 border-t border-white/[0.06]">
              <AppleHealthDerivedAttribution />
            </div>
          )}
        </div>

        {/* Floating Coach Chat */}
        <PlanCoachChat />
      </div>
    </AppLayout>
  );
}

/**
 * Coach Dashboard - View for coach users
 * Shows athlete adherence, training trends, and risk flags
 */
function CoachDashboard() {
  const { t } = useTranslation();
  const { athletes, selectedAthlete, selectedAthleteId, setSelectedAthleteId, isLoading: athletesLoading } = useAthleteSelection();
  const dashboardResult = useCoachDashboardData(selectedAthleteId ?? undefined);
  const data = dashboardResult as { 
    adherence_pct: number; 
    load_trend: 'stable' | 'increasing' | 'decreasing'; 
    risk_level: 'low' | 'medium' | 'high';
    adherence_trend: number[];
    weekly_loads: WeeklyLoad[];
    risks: Risk[];
    isLoading?: boolean;
    error?: unknown;
  };

  const loadTrend = getLoadTrendDisplay(data.load_trend);
  const isLoadingDashboard = data.isLoading ?? false;
  const hasError = !!data.error;

  return (
    <AppLayout>
      <div className="space-y-6">

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-[clamp(1.25rem,3vw,1.5rem)] font-semibold text-primary">{t('dashboard.coachDashboard.title')}</h1>
            <p className="text-muted-foreground mt-1">
              {t('dashboard.coachDashboard.subtitle')}
            </p>
          </div>
          
          <div className="bg-card rounded-lg border px-3 py-2 inline-flex items-center gap-3">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">{t('dashboard.coachDashboard.activeAthlete')}</span>
            {athletesLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">{t('dashboard.coachDashboard.loading')}</span>
              </div>
            ) : (
              <Select 
                value={selectedAthleteId ?? undefined} 
                onValueChange={setSelectedAthleteId}
                disabled={athletes.length === 0}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder={t('dashboard.coachDashboard.selectAthlete')}>
                    {selectedAthlete && (
                      <div className="flex items-center gap-2">
                        <Avatar className="h-5 w-5">
                          <AvatarFallback className="text-xs bg-accent text-accent-foreground">
                            {getAvatarInitials(selectedAthlete)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm">{selectedAthlete.name}</span>
                      </div>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {athletes.map((athlete) => (
                    <SelectItem key={athlete.id} value={athlete.id}>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-5 w-5">
                          <AvatarFallback className="text-xs bg-accent text-accent-foreground">
                            {getAvatarInitials(athlete)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm">{athlete.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {isLoadingDashboard && (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>{t('dashboard.coachDashboard.loadingData')}</span>
            </div>
          </div>
        )}

        {hasError && !isLoadingDashboard && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
            <p className="font-medium text-destructive">{t('dashboard.coachDashboard.errorTitle')}</p>
            <p className="text-sm text-muted-foreground mt-1">{t('dashboard.coachDashboard.errorSubtitle')}</p>
          </div>
        )}

        {!isLoadingDashboard && !hasError && (
          <>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 lg:col-span-4">
                <CoachKpiCard
                  label={t('dashboard.coachDashboard.adherenceLabel')}
                  value={`${data.adherence_pct}%`}
                  subtext={t('dashboard.coachDashboard.adherenceSub')}
                  variant={getAdherenceVariant(data.adherence_pct)}
                  metricKey="coachAdherence"
                />
              </div>
              <div className="col-span-12 lg:col-span-4">
                <CoachKpiCard
                  label={t('dashboard.coachDashboard.loadLabel')}
                  value={loadTrend.value}
                  subtext={t('dashboard.coachDashboard.loadSub')}
                  variant="default"
                  metricKey="coachTrainingLoad"
                />
              </div>
              <div className="col-span-12 lg:col-span-4">
                <CoachKpiCard
                  label={t('dashboard.coachDashboard.riskLabel')}
                  value={data.risk_level.charAt(0).toUpperCase() + data.risk_level.slice(1)}
                  subtext={t('dashboard.coachDashboard.riskSub')}
                  variant={getRiskVariant(data.risk_level)}
                  metricKey="coachRisk"
                />
              </div>
            </div>

            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 lg:col-span-6">
                <AdherenceChart data={data.adherence_trend} />
              </div>
              <div className="col-span-12 lg:col-span-6">
                <WeeklyLoadChart data={data.weekly_loads} />
              </div>
            </div>

            <RiskList risks={data.risks} />
          </>
        )}
      </div>
    </AppLayout>
  );
}

/**
 * Role-aware Dashboard
 */
export default function Dashboard() {
  const { user } = useAuth();
  const role = user?.role ?? 'athlete';

  if (role === 'coach') {
    return <CoachDashboard />;
  }

  return <AthleteDashboard />;
}
